import * as React from 'react';

import { DataGridColumn } from '@rematter/ui-web';

import { useTranslations } from '../../../i18n/useTranslations';
import { WorkOrderRecord } from '../../../types/workOrder';
import { WorkOrderStatusBadge } from '../WorkOrderStatusBadge';

import { formatZonedDateTime } from '~/shared/utils/dateTime';

type UseWorkOrdersColumnsArgs = {
  onRowClick?: (cell: { row: WorkOrderRecord }) => void;
};

export const useWorkOrdersColumns = ({
  onRowClick,
}: UseWorkOrdersColumnsArgs = {}): DataGridColumn<WorkOrderRecord>[] => {
  const t = useTranslations();

  return React.useMemo(() => {
    const columns: DataGridColumn<WorkOrderRecord>[] = [
      {
        key: 'workOrderNumber',
        header: t.workOrdersView.table.columns.workOrderNumber,
        onClick: onRowClick,
        cell: ({ row }) => (
          <span className="font-medium">{row.workOrderNumber}</span>
        ),
        width: 150,
        sortable: true,
      },
      {
        key: 'status',
        header: t.workOrdersView.table.columns.status,
        onClick: onRowClick,
        cell: ({ row }) => <WorkOrderStatusBadge status={row.status} />,
        width: 120,
        sortable: true,
      },
      {
        key: 'processingType',
        header: t.workOrdersView.table.columns.processingType,
        onClick: onRowClick,
        cell: ({ row }) => (
          <div className="flex flex-col">
            <span>{row.processingTypeName || '—'}</span>
            {row.processingTypeCode && (
              <span className="text-xs text-neutral/60">
                {row.processingTypeCode}
              </span>
            )}
          </div>
        ),
        width: 180,
      },
      {
        key: 'facility',
        header: t.workOrdersView.table.columns.facility,
        onClick: onRowClick,
        cell: ({ row }) => <span>{row.facilityName || '—'}</span>,
        width: 180,
      },
      {
        key: 'scheduledStart',
        header: t.workOrdersView.table.columns.scheduledStart,
        onClick: onRowClick,
        cell: ({ row }) => (
          <span className="text-sm">
            {formatZonedDateTime(row.scheduledStartDate)}
          </span>
        ),
        width: 130,
        sortable: true,
      },
      {
        key: 'createdAt',
        header: t.workOrdersView.table.columns.createdAt,
        onClick: onRowClick,
        cell: ({ row }) => (
          <span className="text-sm text-neutral/60">
            {formatZonedDateTime(row.createdAt)}
          </span>
        ),
        width: 120,
        sortable: true,
      },
    ];

    return columns;
  }, [t, onRowClick]);
};
